import apiClient from '../axios.js';

/**
 * 財務管理 API
 */
export const billingService = {
  /**
   * 取得帳單總覽
   * @param {Object} params - 查詢參數
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @returns {Promise<Object>} 帳單總覽資料
   */
  getBillingOverview: async (params = {}) => {
    const response = await apiClient.get('/system/billing/overview', { params });
    return response;
  },

  /**
   * 取得付款記錄列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁數量
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD）
   * @param {string} params.clientId - 客戶 ID
   * @param {string} params.status - 付款狀態（completed, failed, refunded）
   * @param {string} params.paymentType - 付款方式（manual, ecpay）
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @param {string} params.search - 搜尋關鍵字
   * @returns {Promise<Object>} 付款記錄列表
   */
  getPaymentRecords: async (params = {}) => {
    const response = await apiClient.get('/system/billing/payment-records', { params });
    return response;
  },

  /**
   * 匯出付款記錄
   * @param {Object} params - 查詢參數
   * @param {string} params.format - 匯出格式（excel, csv）
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD）
   * @param {string} params.clientId - 客戶 ID
   * @param {string} params.status - 付款狀態
   * @param {string} params.paymentType - 付款方式
   * @param {string} params.platformId - 平台 ID
   * @param {string} params.search - 搜尋關鍵字
   * @returns {Promise<Blob>} 檔案 Blob
   */
  exportPaymentRecords: async (params = {}) => {
    const response = await apiClient.get('/system/billing/payment-records/export', {
      params,
      responseType: 'blob'
    });
    return response;
  },

  /**
   * 取得月營收報表
   * @param {Object} params - 查詢參數
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD，必填）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD，必填）
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @returns {Promise<Object>} 月營收報表資料
   */
  getMonthlyRevenueReport: async (params) => {
    const response = await apiClient.get('/system/billing/reports/monthly-revenue', { params });
    return response;
  },

  /**
   * 取得客戶付費狀態統計
   * @param {Object} params - 查詢參數
   * @param {string} params.date - 統計日期（YYYY-MM-DD，預設今天）
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @returns {Promise<Object>} 客戶付費狀態統計資料
   */
  getPaymentStatusReport: async (params = {}) => {
    const response = await apiClient.get('/system/billing/reports/payment-status', { params });
    return response;
  },

  /**
   * 取得訂閱續約率分析
   * @param {Object} params - 查詢參數
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD，必填）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD，必填）
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @returns {Promise<Object>} 訂閱續約率分析資料
   */
  getRenewalRateReport: async (params) => {
    const response = await apiClient.get('/system/billing/reports/renewal-rate', { params });
    return response;
  },

  /**
   * 取得應收帳款帳齡分析
   * @param {Object} params - 查詢參數
   * @param {string} params.date - 分析日期（YYYY-MM-DD，預設今天）
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @returns {Promise<Object>} 應收帳款帳齡分析資料
   */
  getAgingAnalysisReport: async (params = {}) => {
    const response = await apiClient.get('/system/billing/reports/aging-analysis', { params });
    return response;
  },

  /**
   * 取得付款方式分布統計
   * @param {Object} params - 查詢參數
   * @param {string} params.startDate - 開始日期（YYYY-MM-DD，必填）
   * @param {string} params.endDate - 結束日期（YYYY-MM-DD，必填）
   * @param {string} params.platformId - 平台 ID（系統帳號可選）
   * @returns {Promise<Object>} 付款方式分布統計資料
   */
  getPaymentMethodDistributionReport: async (params) => {
    const response = await apiClient.get('/system/billing/reports/payment-method-distribution', { params });
    return response;
  },

  /**
   * 匯出財務報表
   * @param {Object} params - 查詢參數
   * @param {string} params.reportType - 報表類型（必填）
   * @param {string} params.format - 匯出格式（excel, pdf）
   * @param {string} params.startDate - 開始日期（部分報表需要）
   * @param {string} params.endDate - 結束日期（部分報表需要）
   * @param {string} params.date - 統計日期（部分報表需要）
   * @param {string} params.platformId - 平台 ID
   * @returns {Promise<Blob>} 檔案 Blob
   */
  exportReport: async (params) => {
    const response = await apiClient.get('/system/billing/reports/export', {
      params,
      responseType: 'blob'
    });
    return response;
  }
};
